import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiFileText, FiDownload, FiCheck, FiClock, FiChevronRight,
  FiFilter, FiRefreshCw
} from 'react-icons/fi';
import api from '../api';

export default function Billing() {
  const navigate = useNavigate();
  const [billings, setBillings] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0
  });
  const [filter, setFilter] = useState({
    startMonth: '',
    endMonth: '',
    status: ''
  });
  const [loading, setLoading] = useState(true);

  const loadBillings = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filter.startMonth && { startMonth: filter.startMonth }),
        ...(filter.endMonth && { endMonth: filter.endMonth }),
        ...(filter.status && { status: filter.status })
      };

      const res = await api.get('/api/billing', { params });
      setBillings(res.data.billings || []);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Failed to load billings:', error);
      alert('정산 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filter.startMonth, filter.endMonth, filter.status]);

  useEffect(() => {
    loadBillings();
  }, [loadBillings]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const resetFilter = () => {
    setFilter({ startMonth: '', endMonth: '', status: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDownloadExcel = async (billingId) => {
    try {
      const res = await api.get(`/api/billing/${billingId}/hometax-excel`, {
        responseType: 'blob'
      });

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hometax_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download excel error:', error);
      alert('엑셀 다운로드에 실패했습니다.');
    }
  };

  const handleConfirm = async (billingId) => {
    if (!window.confirm('정산을 확정하시겠습니까? 확정 후에는 수정할 수 없습니다.')) {
      return;
    }

    try {
      await api.post(`/api/billing/${billingId}/confirm`);
      alert('정산이 확정되었습니다.');
      loadBillings();
    } catch (error) {
      console.error('Confirm billing error:', error);
      alert(error.response?.data?.error || '정산 확정에 실패했습니다.');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      generated: { text: '생성', color: '#F59E0B', icon: FiClock },
      confirmed: { text: '확정', color: '#10B981', icon: FiCheck }
    };

    const badge = badges[status] || badges.generated;
    const Icon = badge.icon;

    return (
      <span
        className="status-badge"
        style={{ backgroundColor: `${badge.color}20`, color: badge.color }}
      >
        <Icon size={14} />
        {badge.text}
      </span>
    );
  };

  return (
    <div className="billing-page">
      <div className="page-header">
        <div>
          <h2>월 정산 관리</h2>
          <p>월별 정산 내역을 확인하고 현금영수증을 발급하세요</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => navigate('/points')}>
            포인트 관리
          </button>
        </div>
      </div>

      {/* 필터 */}
      <div className="filter-section">
        <div className="filter-group">
          <label>시작월</label>
          <input
            type="month"
            value={filter.startMonth}
            onChange={(e) => handleFilterChange('startMonth', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>종료월</label>
          <input
            type="month"
            value={filter.endMonth}
            onChange={(e) => handleFilterChange('endMonth', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>상태</label>
          <select
            value={filter.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">전체</option>
            <option value="generated">생성</option>
            <option value="confirmed">확정</option>
          </select>
        </div>

        <button className="btn-reset" onClick={resetFilter}>
          <FiFilter />
          필터 초기화
        </button>

        <button className="btn-secondary" onClick={loadBillings}>
          <FiRefreshCw />
          새로고침
        </button>
      </div>

      {/* 정산 목록 */}
      {loading ? (
        <div className="loading">로딩 중...</div>
      ) : billings.length === 0 ? (
        <div className="empty-state">
          <FiFileText size={48} color="#9CA3AF" />
          <p>정산 내역이 없습니다.</p>
          <p className="empty-hint">매월 1일에 자동으로 정산이 생성됩니다.</p>
        </div>
      ) : (
        <>
          <div className="billings-grid">
            {billings.map((billing) => (
              <div
                key={billing._id}
                className="billing-card"
                onClick={() => navigate(`/billing/${billing._id}`)}
              >
                <div className="billing-header">
                  <div className="billing-month">
                    <FiFileText size={20} />
                    <span>{billing.billingMonth}</span>
                  </div>
                  {getStatusBadge(billing.status)}
                </div>

                <div className="billing-stats">
                  <div className="stat-row">
                    <span className="stat-label">플랜</span>
                    <span className="stat-value">{billing.planType.toUpperCase()}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">푸시</span>
                    <span className="stat-value">
                      {billing.usageBreakdown.push.count}건
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">알림톡</span>
                    <span className="stat-value">
                      {billing.usageBreakdown.alimtalk.count}건
                    </span>
                  </div>
                  <div className="stat-row total">
                    <span className="stat-label">총 금액</span>
                    <span className="stat-value highlight">
                      {billing.totalUsageAmount.toLocaleString()}원
                    </span>
                  </div>
                </div>

                <div className="billing-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn-download"
                    onClick={() => handleDownloadExcel(billing._id)}
                  >
                    <FiDownload />
                    홈택스 엑셀
                  </button>
                  {billing.status === 'generated' && (
                    <button
                      className="btn-confirm"
                      onClick={() => handleConfirm(billing._id)}
                    >
                      <FiCheck />
                      확정하기
                    </button>
                  )}
                </div>

                <div className="billing-footer">
                  <span className="view-detail">
                    상세보기
                    <FiChevronRight size={16} />
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* 페이지네이션 */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn-page"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                이전
              </button>

              <span className="page-info">
                {pagination.page} / {pagination.pages} 페이지
              </span>

              <button
                className="btn-page"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                다음
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
